/*** Thread weather — how much a tension presses, inside ONE meeting (#89)
 *
 * Five crans, weather words chosen with Yohan on 16/09/2026 (REU89-D4). They
 * are NOT a scale of rain: each says something the others don't, and the order
 * below is the one the "by priority" view uses (REU89-D12).
 *
 * 🔑 A weather lives in the meeting step, never on the thread (REU89-D1). A
 * tension carried over to the next meeting comes back unclassified: an urgency
 * ages, and today's facilitator says what presses today (REU89-D2).
 *
 * 🔑 Nothing is ever written for the default. An unclassified tension DISPLAYS
 * as a shower (REU89-D5), so meetings held before this feature stay readable
 * and no data migration was needed.
 */

export enum ThreadWeather {
  // "Le bulletin annonce le temps" — nothing to settle, an information for the
  // circle. First in the list on purpose: it is said in three minutes and stops
  // weighing on the meeting.
  Forecast = 'Forecast',
  // "Le tonnerre gronde" — everything stops, it is settled in this meeting.
  Storm = 'Storm',
  // "La pluie s'installe" — important, settled without delay.
  Rain = 'Rain',
  // "Une pluie fine qui passe" — the ordinary case, settled in the flow.
  Shower = 'Shower',
  // "Le ciel se dégage" — not a problem but an opening; it can wait.
  SunnySpell = 'SunnySpell',
}

// The order of the "by priority" view, and the order inside a group of the
// "by role" and "by member" views (REU89-D10, D11, D12).
export const threadWeatherOrder: ThreadWeather[] = [
  ThreadWeather.Forecast,
  ThreadWeather.Storm,
  ThreadWeather.Rain,
  ThreadWeather.Shower,
  ThreadWeather.SunnySpell,
]

export const defaultThreadWeather = ThreadWeather.Shower

// Rank of a weather, unknown values included: a value written by a newer
// version must never drop a tension out of the list, so it falls back to the
// default's rank rather than to -1 or Infinity.
export function threadWeatherRank(weather?: ThreadWeather | null): number {
  const index = threadWeatherOrder.indexOf(weather as ThreadWeather)
  return index === -1 ? threadWeatherOrder.indexOf(defaultThreadWeather) : index
}

// What a step holds: the weather of the tensions someone has classified today.
// A missing entry is not an error, it is the normal state (REU89-D5).
export type ThreadsWeather = Record<string, ThreadWeather>

// The four reading orders of a tensions step (REU89-D7). Free is the default
// and the only one that writes `threadsIds`: the three others are lenses.
export enum MeetingThreadsSortMode {
  Free = 'Free',
  Role = 'Role',
  Member = 'Member',
  Priority = 'Priority',
}

export const defaultMeetingThreadsSortMode = MeetingThreadsSortMode.Free
